import { AdAnalyticsMedia, AdAnalyticsPlacement, AdAnalyticsPlatform, Project } from '@kaizenplatform/hugot-entity';
import IProject from '@kaizenplatform/hugot-entity/dist/entity/IProject';
import { List } from 'immutable';

interface IAdAnalyticsAd {
  adVideoId: string | null;
  title: string;
  project: IProject | null;
  platforms: List<AdAnalyticsPlatform>;
  medias: List<AdAnalyticsMedia>;
  placements: List<AdAnalyticsPlacement>;
  impressions: number;
  clicks: number;
  currency: string;
  spend: number;
  mobileAppInstalls: number | null;
  conversions: number;
  conversionsKey: string | null;
  viewThroughConversions: number;
  cvr: number;
  ctr: number;
  cpc: number;
  cpm: number;
  cpa: number;
  cpaWithViewThroughConversions: number;
  videoP25Watched: number;
  videoP50Watched: number;
  videoP75Watched: number;
  videoP95Watched: number;
  videoP100Watched: number;
  duration: number;
  videoUrl: string;
  thumbnailImageUrl: string;
}

class CreativePerformanceBrowsingAd {
  public readonly adVideoId: string | null;
  public readonly title: string;
  public readonly project: Project | null;
  public readonly platforms: List<AdAnalyticsPlatform>;
  public readonly medias: List<AdAnalyticsMedia>;
  public readonly placements: List<AdAnalyticsPlacement>;
  public readonly impressions: number;
  public readonly clicks: number;
  public readonly currency: string;
  public readonly spend: number;
  public readonly mobileAppInstalls: number | null;
  public readonly conversions: number;
  public readonly conversionsKey: string | null;
  public readonly viewThroughConversions: number;
  public readonly videoP25Watched: number | null;
  public readonly videoP50Watched: number | null;
  public readonly videoP75Watched: number | null;
  public readonly videoP95Watched: number | null;
  public readonly videoP100Watched: number | null;
  public readonly cvr: number;
  public readonly ctr: number | null;
  public readonly cpc: number | null;
  public readonly cpm: number | null;
  public readonly cpa: number | null;
  public readonly cpaWithViewThroughConversions: number | null;
  public readonly duration: number;
  public readonly videoUrl?: string;
  public readonly thumbnailImageUrl: string;

  constructor({
    title,
    project,
    platforms,
    medias,
    placements,
    impressions,
    clicks,
    currency,
    spend,
    mobileAppInstalls,
    conversions,
    conversionsKey,
    viewThroughConversions,
    videoP25Watched,
    videoP50Watched,
    videoP75Watched,
    videoP95Watched,
    videoP100Watched,
    cvr,
    ctr,
    cpc,
    cpm,
    cpa,
    cpaWithViewThroughConversions,
    duration,
    videoUrl,
    thumbnailImageUrl,
  }: {
    title: string;
    project: Project | null;
    platforms: List<AdAnalyticsPlatform>;
    medias: List<AdAnalyticsMedia>;
    placements: List<AdAnalyticsPlacement>;
    impressions: number;
    clicks: number;
    currency: string;
    spend: number;
    mobileAppInstalls: number | null;
    conversions: number;
    conversionsKey: string | null;
    viewThroughConversions: number;
    videoP25Watched: number;
    videoP50Watched: number;
    videoP75Watched: number;
    videoP95Watched: number;
    videoP100Watched: number;
    cvr: number;
    ctr: number;
    cpc: number;
    cpm: number;
    cpa: number;
    cpaWithViewThroughConversions: number;
    duration: number;
    videoUrl: string;
    thumbnailImageUrl: string;
  }) {
    this.adVideoId = null;
    this.title = title;
    this.project = project;
    this.platforms = platforms;
    this.medias = medias;
    this.placements = placements;
    this.impressions = impressions;
    this.clicks = clicks;
    this.currency = currency;
    this.spend = spend;
    this.mobileAppInstalls = mobileAppInstalls;
    this.conversions = conversions;
    this.conversionsKey = conversionsKey;
    this.viewThroughConversions = viewThroughConversions;
    this.videoP25Watched = videoP25Watched;
    this.videoP50Watched = videoP50Watched;
    this.videoP75Watched = videoP75Watched;
    this.videoP95Watched = videoP95Watched;
    this.videoP100Watched = videoP100Watched;
    this.cvr = cvr;
    this.ctr = ctr;
    this.cpc = cpc;
    this.cpm = cpm;
    this.cpa = cpa;
    this.cpaWithViewThroughConversions = cpaWithViewThroughConversions;
    this.duration = duration;
    this.videoUrl = videoUrl;
    this.thumbnailImageUrl = thumbnailImageUrl;
  }

  public toJSON = (): Record<string, any> => ({
    title: this.title,
    project: this.project && this.project.toJSON(),
    platforms: this.platforms && this.platforms.toJSON(),
    medias: this.medias && this.medias.toJSON(),
    placements: this.placements && this.placements.toJSON(),
    impressions: this.impressions,
    clicks: this.clicks,
    currency: this.currency,
    spend: this.spend,
    mobileAppInstalls: this.mobileAppInstalls,
    conversions: this.conversions,
    conversionsKey: this.conversionsKey,
    viewThroughConversions: this.viewThroughConversions,
    videoP25Watched: this.videoP25Watched,
    videoP50Watched: this.videoP50Watched,
    videoP75Watched: this.videoP75Watched,
    videoP95Watched: this.videoP95Watched,
    videoP100Watched: this.videoP100Watched,
    cvr: this.cvr,
    ctr: this.ctr,
    cpc: this.cpc,
    cpm: this.cpm,
    cpa: this.cpa,
    cpaWithViewThroughConversions: this.cpaWithViewThroughConversions,
    duration: this.duration,
    videoUrl: this.videoUrl,
    thumbnailImageUrl: this.thumbnailImageUrl,
  });

  public static fromJSON = (json: IAdAnalyticsAd): CreativePerformanceBrowsingAd => {
    const {
      title,
      project,
      platforms,
      medias,
      placements,
      impressions,
      clicks,
      currency,
      spend,
      mobileAppInstalls,
      conversions,
      conversionsKey,
      viewThroughConversions,
      videoP25Watched,
      videoP50Watched,
      videoP75Watched,
      videoP95Watched,
      videoP100Watched,
      cvr,
      ctr,
      cpc,
      cpm,
      cpa,
      cpaWithViewThroughConversions,
      duration,
      videoUrl,
      thumbnailImageUrl,
    } = Object.assign({}, json);

    if (typeof title !== 'string') throw new Error('.title must be a string');
    if (typeof impressions !== 'number') throw new Error('.impressions must be a number');
    if (typeof clicks !== 'number') throw new Error('.clicks must be a number');
    if (typeof currency !== 'string') throw new Error('.currency must be a string');
    if (typeof spend !== 'number') throw new Error('.spend must be a number');
    if (mobileAppInstalls !== null && typeof mobileAppInstalls !== 'number')
      throw new Error('.mobileAppInstalls must be a number');
    if (typeof conversions !== 'number') throw new Error('.conversions must be a number');
    if (conversionsKey !== null && typeof conversionsKey !== 'string')
      throw new Error('.conversionsKey must be a string');
    if (typeof viewThroughConversions !== 'number') throw new Error('.viewThroughConversions must be a number');
    if (typeof cvr !== 'number') throw new Error('.cvr must be a number');
    if (ctr !== null && typeof ctr !== 'number') throw new Error('.ctr must be a number');
    if (cpc !== null && typeof cpc !== 'number') throw new Error('.cpc must be a number');
    if (cpm !== null && typeof cpm !== 'number') throw new Error('.cpm must be a number');
    if (cpa !== null && typeof cpa !== 'number') throw new Error('.cpa must be a number');
    if (cpaWithViewThroughConversions !== null && typeof cpaWithViewThroughConversions !== 'number')
      throw new Error('.cpaWithViewThroughConversions must be a number');
    if (typeof duration !== 'number') throw new Error('.duration must be a number');
    if (videoUrl !== undefined && typeof videoUrl !== 'string') throw new Error('.videoUrl must be a string');
    if (typeof thumbnailImageUrl !== 'string') throw new Error('.thumbnailImageUrl must be a string');

    if (!Array.isArray(platforms)) throw new Error('.platforms must be an array');
    if (!Array.isArray(medias)) throw new Error('.medias must be an array');
    if (!Array.isArray(placements)) throw new Error('.placements must be an array');

    const _project = project ? Project.fromJSON(project) : null;
    const _platforms = List(platforms).map((platform: AdAnalyticsPlatform) => AdAnalyticsPlatform.fromJSON(platform));
    const _medias = List(medias).map((media: AdAnalyticsMedia) => AdAnalyticsMedia.fromJSON(media));
    const _placements = List(placements).map((placement: AdAnalyticsPlacement) =>
      AdAnalyticsPlacement.fromJSON(placement),
    );

    return new CreativePerformanceBrowsingAd({
      title,
      project: _project,
      platforms: _platforms,
      medias: _medias,
      placements: _placements,
      impressions,
      clicks,
      currency,
      spend,
      mobileAppInstalls,
      conversions,
      conversionsKey,
      viewThroughConversions,
      videoP25Watched,
      videoP50Watched,
      videoP75Watched,
      videoP95Watched,
      videoP100Watched,
      cvr,
      ctr,
      cpc,
      cpm,
      cpa,
      cpaWithViewThroughConversions,
      duration,
      videoUrl,
      thumbnailImageUrl,
    });
  };
}

export default CreativePerformanceBrowsingAd;
